import type { HandleType } from '@kanda-libs/ks-component-ts';
import IAR from './terms/iar';
import Schedules from './terms/schedules';
import Copy from './terms/sections';
import type { PdfComponent } from './TermsAndConditionsRender/components/typed';

export const SIGNEE_FIELD = {
  label: 'Signee Name',
  name: 'signee',
  placeholder: "The signee's full name",
  autoCapitalize: 'on',
  autoCorrect: 'off',
  autoComplete: 'name',
  wrapperProps: {
    className: 'mt-6',
  },
  validation: {
    required: {
      value: true,
      message: 'Signee name is required',
    },
    minLength: {
      value: 1,
      message: 'Signee name must be longer than 1 character',
    },
  },
};

export const AUTH_FIELD = {
  name: 'authorised',
  handle: 'checkbox',
  fieldText: 'I am duly authorised to sign this contract',
};

export const AUTH_FIELD_VALIDATION = {
  required: {
    value: true,
    message: 'You must be authorised to sign this contract',
  },
};

export const TAC_FIELD = {
  name: 'tac',
  handle: 'checkbox' as HandleType,
  fieldText: "I have read and agree to Ideal4Finance's terms & conditions",
};

export const TAC_FIELD_VALIDATION = {
  required: {
    value: true,
    message: 'You must agree to terms and conditions',
  },
};

export const IAR_FIELD = {
  name: 'becomeIar',
  handle: 'checkbox',
  fieldText: 'I agree to become an IAR of Ideal4Finance ',
};

export const IAR_FIELD_VALIDATION = {
  required: {
    value: true,
    message: 'You must agree to become and IAR of Ideal4Finance',
  },
};

export const INITIAL_HEADER: PdfComponent[] = [
  {
    type: 'altText',
    id: 'initialSection',
    content: ['This Agreement', ' is made on {{CURRENT_DATE}}'],
  },
  {
    type: 'altText',
    id: 'between',
    content: ['Between:'],
    boldFirst: true,
  },
  {
    type: 'altText',
    id: 'kanda',
    content: [
      'Kanda Products & Services Ltd,',
      ' a company registered in England and Wales',
      ' (Company Number 11330964)',
      ' whose registered office is at',
      ' 17 High Street, Henley-In-Arden, Warwickshire, England, B95 5AA',
      ' ({{SECTION_ID}});',
    ],
  },
  {
    type: 'altText',
    id: 'and',
    content: ['And:'],
    boldFirst: true,
  },
];

export const LIMITED_PARTIES: PdfComponent[] = [
  ...INITIAL_HEADER,
  {
    type: 'altText',
    id: 'company',
    content: [
      '{{COMPANY_NAME}}',
      ' a company registered in England and Wales',
      ' (Company Number {{IDENTIFIER}})',
      ' whose registered office is at',
      ' {{COMPANY_ADDRESS}}',
      ' ({{SECTION_ID_SUB}}).',
    ],
  },
];

export const SOLE_PARTIES: PdfComponent[] = [
  ...INITIAL_HEADER,
  {
    type: 'altText',
    id: 'company',
    content: [
      '{{IDENTIFIER}}',
      ', an individual who is trading under the name',
      ' {{COMPANY_NAME}}',
      ' and whose registered office is at',
      ' {{COMPANY_ADDRESS}}',
      ' ({{SECTION_ID_SUB}}).',
    ],
  },
];

export const SECTIONS: PdfComponent[] = [
  {
    type: 'altText',
    id: 'parties',
    content: ['(each being a', ' party', ' and together the', ' parties', ').'],
    boldFirst: false,
  },
  {
    type: 'altText',
    id: 'whereas',
    content: ['Whereas'],
  },
  {
    type: 'bulletList',
    id: 'bothPartiesAgree',
    bulletType: 'capitalAlpha',
    content: [
      {
        copy: 'Kanda owns and operates an online platform at https://kanda.co.uk/ providing, inter alia, booking and invoicing services to tradespersons.',
      },
      {
        copy: "The Subscriber is a registered subscriber to Kanda's platform and is subject to the terms and conditions at https://kanda.co.uk/subscriber-terms-of-use/.",
      },
      {
        copy: "Kanda holds a Consumer Credit License and acts as the tradesperson's appointed representative in procuring credit facilities for subscriber tradesperson's customers in relation to the cost of works to be carried out by the subscriber tradespersons.",
      },
      {
        copy: "Kanda maintains relationships with a panel of consumer credit lenders and acts as principal firm under FCA permissions. Subscriber tradespersons operate as Introducer Appointed Representatives (IARs/AR's) of Kanda for the purposes of introducing customer credit applications to these lenders pursuant to Broker Agreements.",
      },
      {
        copy: "Under the Broker Agreements, Kanda may guarantee the performance of certain obligations of the Subscriber (defined in such agreements as the Retailer) and wishes to ensure the Subscriber's compliance with such obligations.",
      },
      {
        copy: 'The Subscriber is willing to accept responsibility for the Retailer obligations under the Broker Agreements and to comply with the obligations that apply to the Retailer in accordance with the terms of this Agreement.',
      },
    ],
  },
  {
    type: 'altText',
    id: 'isAgreed',
    content: ['It is agreed:'],
  },
  ...Object.keys(Copy).map((key) => Copy[key]),
  {
    type: 'whitespace',
    id: 'section-end-whitespace',
    height: 40,
  },
];

export const SIGNEE_BLOCK: PdfComponent[] = [
  {
    type: 'whitespace',
    id: 'pre-kanda-signee-whitespace',
    height: 40,
  },
  {
    type: 'signature',
    id: 'kanda-signee',
    signee: 'Richard Fleeman',
    title: 'CEO / Co-Founder',
    date: '{{CURRENT_DATE}}',
    companyName: 'Kanda Products & Services Ltd.',
  },
  {
    type: 'whitespace',
    id: 'post-kanda-signee-whitespace',
    height: 40,
  },
  {
    type: 'signature',
    id: 'company-signee',
    signee: '{{SIGNEE}}',
    fingerprint: '{{FINGERPRINT}}',
    date: '{{CURRENT_DATE}}',
    companyName: '{{COMPANY_NAME}}',
  },
  {
    type: 'whitespace',
    id: 'post-company-signee-whitespace',
    height: 40,
  },
];

export const SPACER: PdfComponent = {
  type: 'spacer',
  id: 'post-company-signee-spacer',
  margin: 80,
};

export const SCHEDULES: PdfComponent[] = [
  {
    type: 'heading',
    title: 'Schedule 1',
    alignment: 'left',
    id: 'schedule-1-heading',
  },
  {
    type: 'section',
    title: 'Data Protection',
    id: 'schedule1data',
    content: [],
  },
  ...Object.keys(Schedules.Schedule1).map((key) => Schedules.Schedule1[key]),
  {
    type: 'heading',
    title: 'Schedule 2',
    alignment: 'left',
    id: 'schedule-2-heading',
  },
  {
    type: 'section',
    title: 'Permitted Goods and Services and Permitted Sales Channels',
    id: 'schedule2goods',
    content: [],
  },
  ...Object.keys(Schedules.Schedule2).map((key) => Schedules.Schedule2[key]),
  {
    type: 'heading',
    title: 'Schedule 3',
    alignment: 'left',
    id: 'schedule-3-heading',
  },
  {
    type: 'section',
    title: 'Kanda Procedure Guide',
    id: 'schedule3procedure',
    content: [],
  },
  ...Object.keys(Schedules.Schedule3).map((key) => Schedules.Schedule3[key]),
];

export const IAR_HEADER: PdfComponent = {
  type: 'heading',
  title: 'INTRODUCER APPOINTED REPRESENTATIVE AGREEMENT',
  alignment: 'center',
  id: 'iar-heading',
};

export const IAR_TERMS: PdfComponent[] = [
  ...Object.keys(IAR).map((key) => IAR[key]),
  {
    type: 'whitespace',
    id: 'iar-end-whitespace',
    height: 40,
  },
];
